import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CreditProvider } from './context/CreditContext';
import { STORAGE_KEYS } from './utils/constants';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useRTL } from './hooks/useRTL';
import ErrorBoundary from './components/Common/ErrorBoundary';
import RouteErrorBoundary from './components/Common/RouteErrorBoundary';
import ScrollToTop from './components/Common/ScrollToTop';
import Header from './components/Common/Header';
import Footer from './components/Common/Footer';
import CookieConsent from './components/Common/CookieConsent';
import ChatReturnBubble from './components/Consulting/ChatReturnBubble';
import LimitModalProvider from './components/Common/LimitModalProvider';
import Loading from './components/Common/Loading';
import './App.css';
import './utils/i18n';

const HomePage = lazy(() => import('./components/HomePage/HomePage'));
const Login = lazy(() => import('./components/Auth/Login'));
const Register = lazy(() => import('./components/Auth/Register'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const EmailVerification = lazy(() => import('./components/Auth/EmailVerification'));
const EmailVerificationLink = lazy(() => import('./components/Auth/EmailVerificationLink'));
const GoogleAuth = lazy(() => import('./components/Auth/GoogleAuth'));
const Results = lazy(() => import('./components/Results/Results'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const CreditInsufficient = lazy(() => import('./components/Credits/CreditInsufficient'));
const Favorites = lazy(() => import('./components/Results/Favorites'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Returns = lazy(() => import('./pages/Returns'));
const Contact = lazy(() => import('./pages/Contact'));
const NameMeaning = lazy(() => import('./pages/NameMeaning'));
const NameComparePage = lazy(() => import('./pages/NameCompare'));
const NameSuggestion = lazy(() => import('./pages/NameSuggestion'));
const Personality = lazy(() => import('./pages/Personality'));
const Harmony = lazy(() => import('./pages/Harmony'));
const IslamicAnalysis = lazy(() => import('./pages/IslamicAnalysis'));
const Admin = lazy(() => import('./pages/Admin'));
const LiveDemo = lazy(() => import('./pages/LiveDemo'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const TinderMatch = lazy(() => import('./pages/TinderMatch'));
const TinderMatchInvite = lazy(() => import('./pages/TinderMatchInvite'));
const TinderMatchResults = lazy(() => import('./pages/TinderMatchResults'));
const EbookPaymentSuccess = lazy(() => import('./pages/EbookPaymentSuccess'));
const EbookPaymentCancel = lazy(() => import('./pages/EbookPaymentCancel'));
const MyEbooks = lazy(() => import('./components/Ebook/MyEbooks'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Kategori = lazy(() => import('./pages/Kategori'));
const ArastirSubKategori = lazy(() => import('./pages/ArastirSubKategori'));
const SeoNamePage = lazy(() => import('./pages/SeoNamePage'));
const ConsultingRoom = lazy(() => import('./components/Consulting/Room/ConsultingRoom'));

// Hub kararları (newDesign2): /library ve /shop ana sayfadaki kütüphane
// paneline yönlendirilir. Query param'ları korunur (mail/feed UTMs'leri),
// 'open=library' parametresi paneli otomatik açar.
const LibraryToHubRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('open', 'library');
  return <Navigate to={{ pathname: '/', search: params.toString() }} replace />;
};

// Google Merchant feed ve mail linkleri /shop/:slug'a düşer → panel + kitap modalı
const ShopBookToHubRedirect = () => {
  const { slug } = useParams();
  const params = new URLSearchParams();
  params.set('open', 'library');
  params.set('book', slug);
  return <Navigate to={{ pathname: '/', search: params.toString() }} replace />;
};

// Google Analytics hook'u için ayrı component
const AppWithRouter = () => {
  useGoogleAnalytics();
  useRTL();
  const location = useLocation();

  // Admin paneli (tum /madmin/* rotalari): kendi chrome'una (sidebar, admin header,
  // cikis butonu) sahip — site header/footer ile cakismamasi icin gizlenir.
  // (Admin header z-index'i site header'dan yuksek oldugu icin mobilde hamburger
  // menunun uzerine biniyordu; kok nedeni cift chrome.)
  const isAdminArea = location.pathname.startsWith('/madmin');

  // Canlı danışmanlık odası: mobil alt navigasyon "Gönder" butonunu kapattığı
  // için yalnızca /consulting rotasında gizlenir (site header kalır).
  const isConsultingRoom = location.pathname.startsWith('/consulting');

  // Save current path for redirect after auth (skip auth pages)
  useEffect(() => {
    const authPaths = ['/login', '/register', '/verify-email', '/verify-email-code', '/auth/google/callback', '/forgot-password', '/reset-password'];
    if (!authPaths.includes(location.pathname)) {
      localStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_AUTH, location.pathname + location.search);
    }
  }, [location]);
  
  return (
    <div className={`App${isAdminArea ? ' App--chromeless' : ''}${isConsultingRoom ? ' App--chat' : ''}`}>
            <ScrollToTop />
            {!isAdminArea && <Header />}
            <main className={`main-content${isAdminArea ? ' chromeless-main' : ''}${isConsultingRoom ? ' chat-main' : ''}`} id="main-content">
              <Suspense fallback={<Loading />}>
              <Routes>
              <Route path="/" element={<RouteErrorBoundary><HomePage /></RouteErrorBoundary>} />
              {/* Auth Routes */}
              <Route path="/login" element={<RouteErrorBoundary><Login /></RouteErrorBoundary>} />
              <Route path="/register" element={<RouteErrorBoundary><Register /></RouteErrorBoundary>} />
              <Route path="/auth/google/callback" element={<RouteErrorBoundary><GoogleAuth /></RouteErrorBoundary>} />
              <Route path="/forgot-password" element={<RouteErrorBoundary><ForgotPassword /></RouteErrorBoundary>} />
              <Route path="/reset-password" element={<RouteErrorBoundary><ResetPassword /></RouteErrorBoundary>} />
              <Route path="/verify-email" element={<RouteErrorBoundary><EmailVerificationLink /></RouteErrorBoundary>} />
              <Route path="/verify-email-code" element={<RouteErrorBoundary><EmailVerification /></RouteErrorBoundary>} />
              {/* Core Feature Routes */}
              <Route path="/results/:suggestionId?" element={<RouteErrorBoundary><Results /></RouteErrorBoundary>} />
              <Route path="/profile" element={<RouteErrorBoundary><Profile /></RouteErrorBoundary>} />
              <Route path="/consulting" element={<RouteErrorBoundary><ConsultingRoom /></RouteErrorBoundary>} />
              <Route path="/category" element={<RouteErrorBoundary><Kategori /></RouteErrorBoundary>} />
              <Route path="/category/research" element={<RouteErrorBoundary><ArastirSubKategori /></RouteErrorBoundary>} />
              <Route path="/name-meaning" element={<RouteErrorBoundary><NameMeaning /></RouteErrorBoundary>} />
              <Route path="/name-compare" element={<RouteErrorBoundary><NameComparePage /></RouteErrorBoundary>} />
              <Route path="/name-suggestion" element={<RouteErrorBoundary><NameSuggestion /></RouteErrorBoundary>} />
              <Route path="/personality" element={<RouteErrorBoundary><Personality /></RouteErrorBoundary>} />
              <Route path="/harmony" element={<RouteErrorBoundary><Harmony /></RouteErrorBoundary>} />
              <Route path="/analysis" element={<RouteErrorBoundary><IslamicAnalysis /></RouteErrorBoundary>} />
              {/* Credit & Favorites */}
              <Route path="/credits" element={<RouteErrorBoundary><CreditInsufficient /></RouteErrorBoundary>} />
              <Route path="/credits/purchase" element={<RouteErrorBoundary><CreditInsufficient /></RouteErrorBoundary>} />
              <Route path="/credits/insufficient" element={<RouteErrorBoundary><CreditInsufficient /></RouteErrorBoundary>} />
              <Route path="/favorites" element={<RouteErrorBoundary><Favorites /></RouteErrorBoundary>} />
              {/* Static Pages */}
              <Route path="/terms" element={<RouteErrorBoundary><Terms /></RouteErrorBoundary>} />
              <Route path="/privacy" element={<RouteErrorBoundary><Privacy /></RouteErrorBoundary>} />
              <Route path="/impressum" element={<RouteErrorBoundary><Impressum /></RouteErrorBoundary>} />
              <Route path="/returns" element={<RouteErrorBoundary><Returns /></RouteErrorBoundary>} />
              <Route path="/contact" element={<RouteErrorBoundary><Contact /></RouteErrorBoundary>} />
              {/* Admin Routes */}
              <Route path="/madmin" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/payments" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/users" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/consulting" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/consulting/dashboard" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/consulting/requests" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/consulting/sessions" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/consulting/feedback" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/calendar" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/ebooks" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/email-broadcast" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/lead-magnets" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/settings" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/messages" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/names" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              <Route path="/madmin/activity" element={<RouteErrorBoundary><Admin /></RouteErrorBoundary>} />
              {/* TikTok canlı yayın sayfası — sadece admin, reklamsız tek sayfa araçlar */}
              <Route path="/madmin/live" element={<RouteErrorBoundary><LiveDemo /></RouteErrorBoundary>} />
              {/* E-Kitap Shop Routes — hub kararları (newDesign2):
    /shop ve /shop/:slug ana sayfadaki kütüphane paneline yönlendirilir;
    PayPal dönüş sayfaları ve /my-ebooks korunur (hard linkler bozulmaz). */}
              <Route path="/shop/payment/success" element={<RouteErrorBoundary><EbookPaymentSuccess /></RouteErrorBoundary>} />
              <Route path="/shop/payment/cancel" element={<RouteErrorBoundary><EbookPaymentCancel /></RouteErrorBoundary>} />
              <Route path="/shop/:slug" element={<RouteErrorBoundary><ShopBookToHubRedirect /></RouteErrorBoundary>} />
              <Route path="/shop" element={<RouteErrorBoundary><LibraryToHubRedirect /></RouteErrorBoundary>} />
              <Route path="/my-ebooks" element={<RouteErrorBoundary><MyEbooks /></RouteErrorBoundary>} />
              {/* Ucretsiz PDF kampanyasi — mail linkleri /library ve /free-download
                  adreslerinden ana sayfa paneline yonlenir (query korunur). */}
              <Route path="/library" element={<RouteErrorBoundary><LibraryToHubRedirect /></RouteErrorBoundary>} />
              <Route path="/free-download" element={<LibraryToHubRedirect />} />
              {/* Payment Routes */}
              <Route path="/payment/success" element={<RouteErrorBoundary><PaymentSuccess /></RouteErrorBoundary>} />
              <Route path="/payment/cancel" element={<RouteErrorBoundary><PaymentCancel /></RouteErrorBoundary>} />
              {/* Baby Name Match & SEO Routes */}
              <Route path="/search/meaning/:name" element={<RouteErrorBoundary><SeoNamePage /></RouteErrorBoundary>} />
              <Route path="/baby-name-match" element={<RouteErrorBoundary><TinderMatch /></RouteErrorBoundary>} />
              <Route path="/baby-name-match/invite/:token" element={<RouteErrorBoundary><TinderMatchInvite /></RouteErrorBoundary>} />
              <Route path="/baby-name-match/:sessionId/results" element={<RouteErrorBoundary><TinderMatchResults /></RouteErrorBoundary>} />
              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
        </main>
        {!isAdminArea && !isConsultingRoom && <Footer />}
        {!isAdminArea && !isConsultingRoom && <CookieConsent />}
        {!isAdminArea && !isConsultingRoom && <ChatReturnBubble />}
        <LimitModalProvider />
      </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <CreditProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <AppWithRouter />
            </Router>
          </CreditProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App; 
